import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { motion } from 'framer-motion'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { formlessColors, tonalPalettes } from '@zuto/theme/src/mui/Colors'
import type { InterstitialStage } from '../../../../../types/ui'

const MotionBox = motion.create(Box)

const TrustCard = styled(Box)(() => ({
  borderRadius: '16px',
  border: `2px solid ${formlessColors.borderSubtle}`,
  backgroundColor: tonalPalettes.neutral[100],
  padding: '24px',
  boxShadow: '0 10px 20px rgba(15,23,42,0.04)',
  width: '100%'
}))

type TrustPointsCardProps = {
  stage: InterstitialStage
  isPreapproved?: boolean
}

const TrustPointsCard: React.FC<TrustPointsCardProps> = ({ stage, isPreapproved = false }) => {
  const isComplete = stage === 'complete'

  const trustPoints = [
    {
      icon: CheckCircleIcon,
      text: isPreapproved ? 'Your offer is secure' : 'Checking your eligibility'
    },
    { icon: ShieldOutlinedIcon, text: 'Data transferred safely' },
    { icon: StarBorderIcon, text: 'Dedicated Car Finance Partner' }
  ]
  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{
        margin: '0 auto',
        marginBottom: '32px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        maxWidth: '400px'
      }}
    >
      <TrustCard>
        {trustPoints.map((point, index) => {
          const Icon = point.icon
          const isLast = index === trustPoints.length - 1

          return (
            <MotionBox
              key={point.text}
              initial={{ opacity: 0, x: -10, y: 8, scale: 0.95 }}
              animate={{
                opacity: isComplete ? 1 : 0.65,
                x: 0,
                y: 0,
                scale: 1
              }}
              transition={{
                delay: 0.5 + index * 0.15,
                type: 'spring',
                stiffness: 420,
                damping: 20
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: isLast ? 0 : '12px'
              }}
            >
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: formlessColors.trustIconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Icon sx={{ fontSize: '18px', color: formlessColors.accentGreen }} />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: formlessColors.trustTextStrong
                }}
              >
                {point.text}
              </Typography>
            </MotionBox>
          )
        })}
      </TrustCard>
    </MotionBox>
  )
}

export default TrustPointsCard
